import { getToken, clearToken } from '../utils/authToken';
import { saveBlobWithPicker } from '../utils/downloadBlob';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// BUILD MARKER: Job 4361d940 - 19/Mar/2026 19:25 - Botón suscripción entre Publicar e Imágenes
// P3.1 sesión 2/4 (feb-2026): Dashboard.jsx orquesta subcomponentes en /workspace/.
import { Copy, Check, Download, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import Avatar from './Avatar';
import mermaid from 'mermaid';
import { useLanguage } from '../context/LanguageContext';
import { useWorkspaceImageStore } from '../context/WorkspaceImageContext';
import EmptyWorkspaceMatrix from './EmptyWorkspaceMatrix';
import { PublishAppModal } from './MyApps';
import { COMPONENT_REGISTRY } from './DynamicComponentRenderer';
import { parseJSONActions } from './DamyxActionExecutor';
import { cleanCodeForPreview } from './UnifiedCodePreview';
import ImageGalleryModal from './ImageGalleryModal';
import SubscriptionWidget from './SubscriptionWidget';
import useSubscription from '../hooks/useSubscription';
import axios from 'axios';

import { BACKEND_URL as API_URL } from '../config/api';
import CodeHistoryModal from './CodeHistoryModal';

// Subcomponentes extraídos en el refactor P3.1 sesión 2/4 (feb-2026).
import WorkspaceTopBar from './workspace/WorkspaceTopBar';
import PremiumBanner from './workspace/PremiumBanner';
import CancelSubscriptionModal from './workspace/CancelSubscriptionModal';
import QuickAccessPremium from './workspace/QuickAccessPremium';
import WorkspaceMainContent from './workspace/WorkspaceMainContent';
import GeneratedFilesDrawer from './GeneratedFilesDrawer';
import { downloadConversationAsProject, extractConversationFiles } from '../utils/downloadProject';
import { isDamyxActionBlock } from '../utils/damyxFence';

// Colores de la identidad visual oficial "Dark Tech Elegante"
const COLORS = {
  background: '#1E293B',  // Más claro que antes (#0F172A)
  panel: '#334155',       // Más claro
  border: '#475569',
  textPrimary: '#FFFFFF',
  textSecondary: '#CBD5E1',
  primary: '#3B82F6',
  success: '#22C55E',
  warning: '#EAB308',
  error: '#EF4444',
  info: '#38BDF8',
};

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  securityLevel: 'loose',
  fontFamily: 'ui-monospace, monospace',
});

// MERMAID_TYPES — solo se reconoce 'mermaid' explícito (FASE 13.2 fix).
// const MERMAID_TYPES = ['mermaid', 'flowchart', 'sequencediagram', 'classdiagram', 'statediagram', 'erdiagram', 'gantt', 'pie', 'gitgraph', 'mindmap', 'timeline'];


const Dashboard = ({ 
  conversations, 
  onSelectConversation, 
  currentConversation, 
  chartData, 
  forceInternalWorkspace, 
  onInternalWorkspaceClosed, 
  isWorkspaceExpanded, 
  onExpandWorkspace,
  // Props elevados para persistir estado entre expand/collapse
  latestWorkspaceImage: externalLatestImage,
  setLatestWorkspaceImage: externalSetLatestImage,
  showImageEditor: externalShowEditor,
  setShowImageEditor: externalSetShowEditor
}) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [runningCodeIdx, setRunningCodeIdx] = useState(null);
  
  // Code Version History State
  const [codeVersions, setCodeVersions] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [lastSavedCode, setLastSavedCode] = useState('');
  
  // Workspace Images State
  // 🟥 CUADRO NEGRO 2026-06-30 — imágenes y variantes ahora viven en el
  // WorkspaceImageContext (a nivel App) para SOBREVIVIR al desmontaje de
  // Dashboard cuando el usuario apreta la flechita "Expandir Workspace"
  // (App.js líneas 840/858 renderizan Dashboard en distinta ubicación →
  // React desmonta y monta uno nuevo, perdiendo el useState local).
  // Trash badge — total combinado de imagenes + conversaciones en Papelera
  const [trashBadgeCount, setTrashBadgeCount] = useState(0);
  // 🟥 CUADRO NEGRO 2026-06-28 (rev12) — Fuente única de verdad del
  // Workspace via store global (Context). El listener vive a nivel App
  // y SOBREVIVE a re-mounts de Dashboard (cambio de tab Chat↔Trabajo en
  // mobile, hot reload, etc.). El store persiste en sessionStorage.
  // Desktop / Preview: el flujo previo no cambia, solo migramos la
  // fuente del state. El externalLatestImage/externalSetLatestImage que
  // recibe via props sigue teniendo prioridad por si algún padre lo usa.
  const {
    latestWorkspaceImage: storeLatestImage,
    setLatestWorkspaceImage: storeSetLatestImage,
    showImageEditor: storeShowEditor,
    setShowImageEditor: storeSetShowEditor,
    workspaceImages,
    setWorkspaceImages,
    workspaceVariants,
    setWorkspaceVariants,
  } = useWorkspaceImageStore();
  const latestWorkspaceImage = externalLatestImage !== undefined ? externalLatestImage : storeLatestImage;
  const setLatestWorkspaceImage = externalSetLatestImage || storeSetLatestImage;

  const showImageEditor = externalShowEditor !== undefined ? externalShowEditor : storeShowEditor;
  const setShowImageEditor = externalSetShowEditor || storeSetShowEditor;
  
  const [showGalleryFromHeader, setShowGalleryFromHeader] = useState(false);
  // Cargar de localStorage si el usuario cerró el visor - persiste entre sesiones
  const [userClosedImageViewer, setUserClosedImageViewer] = useState(() => {
    return localStorage.getItem('damyxia_image_viewer_closed') === 'true';
  });
  
  // Estado para mostrar/ocultar el widget de suscripción
  // Persistido en localStorage: una vez oculto, no vuelve
  // Regla oficial: el panel Premium SIEMPRE arranca cerrado. Se abre unica-
  // mente cuando el usuario hace click en el boton 💳 (CreditCard) de la
  // barra superior. Sin memoria entre sesiones, sin localStorage, sin
  // ninguna logica que lo abra automaticamente.
  const [showSubscriptionWidget, setShowSubscriptionWidget] = useState(false);

  // Limpieza one-time de flags residuales (incluido el que yo mismo guardaba
  // antes en `damyxia_show_subscription`). Garantiza que el usuario nunca
  // vuelva a encontrarse el panel abierto al entrar por culpa de un flag
  // persistido de sesiones previas.
  useEffect(() => {
    try {
      localStorage.removeItem('damyxia_show_subscription');
      localStorage.removeItem('damyxia_hide_subscription');
      localStorage.removeItem('damyxia_hide_subscription_migrated_v2');
    } catch (_e) { /* ignore */ }
  }, []);

  // 🟥 CUADRO NEGRO Panel Conversión (feb-2026): el evento global
  // `damyxia:open-subscription-panel` lo escucha ahora el componente
  // `SubscriptionUpgradeOverlayMount` montado a nivel App. Este efecto
  // queda como noop intencional para preservar comentarios históricos.
  // El widget Dashboard `SubscriptionWidget` se sigue abriendo vía el
  // botón 💳 de la WorkspaceTopBar (acceso manual del Premium para ver
  // cuotas), pero los CTAs Premium abren el overlay nuevo.
  
  // Función para ocultar permanentemente
  const hideSubscriptionWidget = () => {
    setShowSubscriptionWidget(false);
  };
  
  // Ocultar cuando se selecciona conversación
  useEffect(() => {
    if (currentConversation) {
      hideSubscriptionWidget();
    }
  }, [currentConversation]);

  // Ocultar cuando se carga una imagen en el workspace
  useEffect(() => {
    if (latestWorkspaceImage) {
      hideSubscriptionWidget();
    }
  }, [latestWorkspaceImage]);

  
  // Verificar si es el creador (case-insensitive)
  const userEmail = (localStorage.getItem('userEmail') || '').toLowerCase().trim();
  const isCreator = ['damyxia@gmail.com', 'marcmarl2@gmail.com', 'manager@damyxia.com', 'support@damyxia.com'].includes(userEmail);
  
  // Guardar en localStorage cuando cambia userClosedImageViewer
  useEffect(() => {
    localStorage.setItem('damyxia_image_viewer_closed', userClosedImageViewer.toString());
  }, [userClosedImageViewer]);
  
  // FORZAR re-render cuando cambia currentConversation
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Cargar imágenes del workspace
  const loadWorkspaceImages = async () => {
    try {
      const token = getToken();
      if (!token || token === 'guest') return;
      
      const response = await axios.get(`${API_URL}/api/workspace/items`, {
      });
      
      const items = response.data.items || [];
      
      // Separar originales y variantes
      const originals = items.filter(item => item.type === 'IMAGE_SOURCE');
      const variants = items.filter(item => item.type === 'IMAGE_VARIANT');
      
      setWorkspaceImages(originals);
      setWorkspaceVariants(variants);
      
      // NUNCA mostrar imagen automáticamente
      // Solo se muestra cuando el usuario la abre manualmente desde la galería
    } catch (error) {
      console.error('Error loading workspace images:', error);
    }
  };
  
  // Obtener variantes de una imagen específica
  const getVariantsForItem = (itemId) => {
    return workspaceVariants.filter(v => v.parent_id === itemId);
  };
  
  // Manejar selección de variante
  const handleSelectVariant = (variant) => {
    setLatestWorkspaceImage(variant);
  };
  
  // Manejar eliminación de item
  const handleDeleteWorkspaceItem = (itemId) => {
    setWorkspaceImages(prev => prev.filter(img => img.id !== itemId));
    setWorkspaceVariants(prev => prev.filter(v => v.id !== itemId && v.parent_id !== itemId));
    
    // Si era la imagen activa, seleccionar otra o limpiar
    if (latestWorkspaceImage?.id === itemId) {
      const remaining = workspaceImages.filter(img => img.id !== itemId);
      if (remaining.length > 0) {
        setLatestWorkspaceImage(remaining[0]);
      } else {
        setLatestWorkspaceImage(null);
        setShowImageEditor(false);
        setUserClosedImageViewer(true); // Marcar como cerrado para evitar que reaparezca
      }
    }
  };
  
  // Cargar imágenes del workspace al montar y cuando cambia la conversación
  useEffect(() => {
    loadWorkspaceImages();
  }, [currentConversation?.messages?.length]);

  // Botón "Refrescar Dashboard" del header — el CustomEvent global dispara un
  // reload inmediato de las imágenes del workspace sin recargar la página.
  useEffect(() => {
    const onRefresh = () => { loadWorkspaceImages(); };
    window.addEventListener('damyxia:refresh-dashboard', onRefresh);
    return () => window.removeEventListener('damyxia:refresh-dashboard', onRefresh);
  }, []);
  
  // Escuchar eventos de publicación de imágenes — side effects locales del
  // Dashboard (carga de listas, scroll mobile). El state global del workspace
  // ya lo maneja el `WorkspaceImageProvider` a nivel App, así que aquí solo
  // disparamos efectos secundarios específicos del Dashboard.
  useEffect(() => {
    const handleImagePublished = async (event) => {
      // Recargar lista de imágenes siempre
      await loadWorkspaceImages();

      try {
        if (event?.detail?.workspaceItem) {
          // Reset del flag "usuario cerró el visor" (state local de Dashboard).
          setUserClosedImageViewer(false);

          // 🟥 CUADRO NEGRO 2026-06-27 (rev8) — Mobile-only: scroll suave
          // al contenedor del workspace tras un microdelay (espera render
          // del viewer).
          try {
            const isMobile = typeof window !== 'undefined'
              && window.matchMedia
              && window.matchMedia('(max-width: 767px)').matches;
            if (isMobile) {
              setTimeout(() => {
                const workspaceEl = document.querySelector(
                  '[data-testid="workspace-main-content"], [data-workspace-root="true"]'
                );
                if (workspaceEl && typeof workspaceEl.scrollIntoView === 'function') {
                  workspaceEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 180);
            }
          } catch { /* no-op: si falla scroll, el comportamiento previo se preserva */ }
        }
      } catch (error) {
        console.error('Error loading latest workspace image:', error);
      }
    };

    window.addEventListener('workspace-image-published', handleImagePublished);
    return () => window.removeEventListener('workspace-image-published', handleImagePublished);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar el badge de Papelera (imagenes + conversaciones) al montar y
  // cuando algo se borre/restaure (evento global 'damyxia:trash-updated').
  useEffect(() => {
    let cancelled = false;
    const loadTrashBadge = async () => {
      try {
        const token = getToken();
        if (!token || token === 'guest') return;
        const res = await axios.get(`${API_URL}/api/trash/access-status`, {
        });
        if (cancelled) return;
        const total = (res.data?.total_in_trash || 0) + (res.data?.total_conversations_in_trash || 0);
        setTrashBadgeCount(total);
      } catch { /* silencioso */ }
    };
    loadTrashBadge();
    const handler = () => loadTrashBadge();
    window.addEventListener('damyxia:trash-updated', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('damyxia:trash-updated', handler);
    };
  }, []);

  // FASE Historial Avanzado: recoger imagen precargada desde /historial
  useEffect(() => {
    const consumePreload = () => {
      try {
        const raw = sessionStorage.getItem('damyxia_history_action');
        if (!raw) return;
        const payload = JSON.parse(raw);
        sessionStorage.removeItem('damyxia_history_action');

        // Sanity: descartar si es demasiado viejo (>60s)
        if (payload.timestamp && Date.now() - payload.timestamp > 60000) return;

        // Construir un workspace item sintetico (data-URL en preview_url)
        const syntheticItem = {
          id: `history_${payload.image_id}_${Date.now()}`,
          type: 'IMAGE_SOURCE',
          linked_image_id: payload.image_id,
          display_name: payload.prompt ? payload.prompt.slice(0, 60) : 'Historial',
          status: 'ready',
          preview_url: payload.data_url,
          created_at: new Date().toISOString(),
          source: 'history',
          history_action: payload.action,   // pro | variants | background | generation_normal
          prompt: payload.prompt || '',
        };
        setLatestWorkspaceImage(syntheticItem);
        setShowImageEditor(true);
        setUserClosedImageViewer(false);
      } catch (e) {
        console.error('Error consumiendo damyxia_history_action:', e);
      }
    };

    // Ejecutar al montar y suscribirse a navigate pushState (no siempre se remonta)
    consumePreload();
    window.addEventListener('damyxia:history-preload', consumePreload);
    return () => window.removeEventListener('damyxia:history-preload', consumePreload);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  useEffect(() => {
    if (currentConversation) {
      setForceUpdate(prev => prev + 1);
      // Double-check: force another render after a short delay to catch race conditions
      const timer = setTimeout(() => {
        setForceUpdate(prev => prev + 1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentConversation, currentConversation?.messages?.length, currentConversation?.messages?.[currentConversation?.messages?.length - 1]?.content]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentMessages = currentConversation ? (currentConversation.messages || []) : [];

  // Fase 12.2: mensajes válidos para preview/workspace (excluye los marcados
  // como `reverted: true` por /api/damyxia/modules/{id}/undo). Esto hace que
  // al hacer Revertir desaparezca también el código de la "Vista Previa React"
  // que se renderiza desde el último code-block del chat.
  const previewMessages = currentMessages.filter((m) => !m.reverted);
  
  // Load history from backend on mount
  useEffect(() => {
    const loadCodeHistory = async () => {
      if (!currentConversation?.id) return;
      
      try {
        const token = getToken();
        if (token && token !== 'guest') {
          const response = await axios.get(
            `${API_URL}/api/code-history/versions/${currentConversation.id}`,
            {}
          );
          setCodeVersions(response.data.versions || []);
        } else {
          // Fallback to localStorage for guests
          const historyKey = `damyxia_code_history_${currentConversation.id}`;
          const savedHistory = JSON.parse(localStorage.getItem(historyKey) || '[]');
          setCodeVersions(savedHistory);
        }
      } catch (error) {
        console.error('Error loading code history:', error);
        // Fallback to localStorage
        try {
          const historyKey = `damyxia_code_history_${currentConversation.id}`;
          const savedHistory = JSON.parse(localStorage.getItem(historyKey) || '[]');
          setCodeVersions(savedHistory);
        } catch {
          setCodeVersions([]);
        }
      }
    };
    
    loadCodeHistory();
  }, [currentConversation?.id]);

  // Save version when code changes
  const saveCodeVersion = async (code) => {
    if (!code || !currentConversation?.id || code === lastSavedCode) return;
    
    const newVersion = {
      id: `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      code: lastSavedCode, // Save the PREVIOUS code
      timestamp: new Date().toISOString()
    };
    
    if (lastSavedCode) {
      const updatedVersions = [newVersion, ...codeVersions].slice(0, 20);
      setCodeVersions(updatedVersions);
      
      // Try to save to backend
      try {
        const token = getToken();
        if (token && token !== 'guest') {
          await axios.post(
            `${API_URL}/api/code-history/save`,
            {
              conversation_id: currentConversation.id,
              code: lastSavedCode
            },
            {}
          );
        } else {
          // Fallback to localStorage
          const historyKey = `damyxia_code_history_${currentConversation.id}`;
          localStorage.setItem(historyKey, JSON.stringify(updatedVersions));
        }
      } catch (error) {
        console.error('Error saving code history:', error);
        // Fallback to localStorage
        try {
          const historyKey = `damyxia_code_history_${currentConversation.id}`;
          localStorage.setItem(historyKey, JSON.stringify(updatedVersions));
        } catch {}
      }
    }
    
    setLastSavedCode(code);
  };

  const handleRestoreVersion = (version) => {
    // Save current code as a new version before restoring
    const currentCode = extractPreviewCode();
    if (currentCode && currentCode !== version.code) {
      saveCodeVersion(currentCode);
    }
    
    // Restore the version - just show toast, code stays in workspace
    setShowHistoryModal(false);
    
    toast.success(t('workspace.versionRestored'));
  };

  const handleDeleteVersion = async (versionId) => {
    const updatedVersions = codeVersions.filter(v => v.id !== versionId);
    setCodeVersions(updatedVersions);
    
    // Try to delete from backend
    try {
      const token = getToken();
      if (token && token !== 'guest') {
        await axios.delete(
          `${API_URL}/api/code-history/version/${versionId}`,
          {}
        );
      } else {
        // Fallback to localStorage
        const historyKey = `damyxia_code_history_${currentConversation?.id}`;
        localStorage.setItem(historyKey, JSON.stringify(updatedVersions));
      }
    } catch (error) {
      console.error('Error deleting code version:', error);
      // Fallback to localStorage
      try {
        const historyKey = `damyxia_code_history_${currentConversation?.id}`;
        localStorage.setItem(historyKey, JSON.stringify(updatedVersions));
      } catch {}
    }
  };

  const isMermaidDiagram = (language, _code) => {
    // FASE 13.2 fix: solo renderizamos Mermaid en el Dashboard si el bloque
    // declara explícitamente `mermaid` como language tag (lo que produce el
    // DiagramGeneratorModal). La detección heurística por palabras del primer
    // line causaba "Syntax error in text — mermaid 11.x" al recibir texto
    // normal del LLM con palabras como "pie" o "timeline".
    return (language || '').toLowerCase() === 'mermaid';
  };

  const getDiagramTitle = (language, code) => {
    const titles = {
      'flowchart': t('dashboard.flowchart'),
      'sequencediagram': t('dashboard.sequence'),
      'classdiagram': t('dashboard.classDiagram'),
      'statediagram': t('dashboard.stateDiagram'),
      'erdiagram': t('dashboard.erDiagram'),
      'gantt': t('dashboard.gantt'),
      'pie': t('dashboard.pie'),
      'gitgraph': t('dashboard.gitGraph'),
      'mindmap': t('dashboard.mindmap'),
      'timeline': t('dashboard.timeline'),
      'mermaid': t('dashboard.diagram')
    };
    
    const lang = language?.toLowerCase() || '';
    const firstLine = code?.split('\n')[0]?.trim()?.toLowerCase() || '';
    
    for (const [key, title] of Object.entries(titles)) {
      if (lang === key || firstLine.startsWith(key)) {
        return title;
      }
    }
    return t('dashboard.diagram');
  };

  // Extract JSX/React code for preview
  const extractPreviewCode = () => {
    const codeBlocks = [];
    
    previewMessages.forEach((msg) => {
      if (msg.role === 'assistant' && msg.content) {
        const regex = /```(jsx?|javascript|tsx?|react|js)?\n?([\s\S]*?)```/g;
        let match;
        
        while ((match = regex.exec(msg.content)) !== null) {
          const lang = match[1] || '';
          const code = match[2] || '';
          
          // Skip non-JSX code
          const lowerLang = lang.toLowerCase();
          if (['bash', 'sh', 'shell', 'css', 'json', 'yaml', 'html', 'sql'].includes(lowerLang)) continue;
          if (code.trim().startsWith('npx ') || code.trim().startsWith('npm ')) continue;
          
          // Check for React code
          const hasReactCode = code.includes('function ') || code.includes('const ') || 
                              code.includes('return (') || code.includes('useState');
          
          if (hasReactCode) {
            const isMainApp = code.includes('function App') || code.includes('const App');
            codeBlocks.push({ code, isMainApp });
          }
        }
      }
    });
    
    if (codeBlocks.length === 0) return '';
    
    // Sort to put App first
    codeBlocks.sort((a, b) => (b.isMainApp ? 1 : 0) - (a.isMainApp ? 1 : 0));
    
    // Clean and combine code
    return codeBlocks.map(block => {
      let code = block.code;
      // Remove ALL import statements (including side-effect imports like import 'tailwindcss')
      code = code.replace(/import\s+.*from\s+['"].*['"];?\n?/g, '');
      code = code.replace(/import\s+['"].*['"];?\n?/g, '');
      // Remove exports
      code = code.replace(/export\s+default\s+/g, '');
      code = code.replace(/export\s+{\s*[\w\s,]*\s*};?\n?/g, '');
      code = code.replace(/export\s+const\s+/g, 'const ');
      code = code.replace(/export\s+function\s+/g, 'function ');
      // Remove file path comments
      code = code.replace(/^\/\/\s*src\/.*\n?/gm, '');
      return code.trim();
    }).join('\n\n');
  };

  const getBlocks = () => {
    const codeBlocks = [];
    const diagrams = [];  // Solo si el usuario pide explícitamente
    const dynamicComponents = [];
    const executeActions = [];
    const chartActions = [];  // Solo si el usuario pide explícitamente
    const imageBlocks = [];   // Para imágenes/diseños generados
    
    previewMessages.forEach((msg, msgIdx) => {
      if (msg.role === 'assistant' && msg.content) {
        
        // Buscar imágenes en el mensaje (URLs de imágenes generadas por DamyxIA)
        const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)|https?:\/\/[^\s<>"{}|\\^`\[\]]+\.(?:png|jpg|jpeg|gif|webp|svg)/gi;
        let imageMatch;
        while ((imageMatch = imageRegex.exec(msg.content)) !== null) {
          const imageUrl = imageMatch[2] || imageMatch[0];
          if (imageUrl && !imageBlocks.find(b => b.url === imageUrl)) {
            imageBlocks.push({
              type: 'image',
              url: imageUrl,
              alt: imageMatch[1] || 'Diseño generado por DamyxIA',
              id: `image-${msgIdx}-${imageBlocks.length}`
            });
          }
        }
        
        // Buscar bloques de código en formato markdown
        // 🟥 P26.33 · El lenguaje puede tener guiones (`damyxia-action`).
        // Antes `(\w+)?` capturaba sólo "damyxia" y el resto ("-action\n{…}")
        // caía como CÓDIGO: la Vista Previa intentaba compilar el JSON del
        // fence y explotaba con "Missing semicolon".
        const regex = /```([\w+.#-]*)\n?([\s\S]*?)```/g;
        let match;
        let blockIdx = 0;

        while ((match = regex.exec(msg.content)) !== null) {
          const language = (match[1] || 'code').toLowerCase().trim();
          const code = match[2].trim();

          // Los fences de acción de DamyxIA NO son código ejecutable.
          if (isDamyxActionBlock(language, code)) {
            continue;
          }

          // Detectar si es código ejecutable por el lenguaje
          const executableLanguages = ['jsx', 'javascript', 'js', 'tsx', 'html', 'react', 'css', 'code'];
          const isExecutableCode = executableLanguages.includes(language);
          
          // Detectar si es un componente React por el contenido
          const isReactComponent = code.includes('function ') || code.includes('const ') || 
                                   code.includes('return (') || code.includes('return <') ||
                                   code.includes('useState') || code.includes('useEffect') ||
                                   code.includes('className=') || code.includes('className="') ||
                                   code.includes('onClick') || code.includes('<div') ||
                                   code.includes('<button') || code.includes('<span');
          
          // Si tiene código React, SIEMPRE procesarlo
          if (isExecutableCode || isReactComponent) {
            const shouldAutoExecute = isReactComponent && code.length > 30;
            
            codeBlocks.push({
              type: 'code',
              language,
              code,
              id: `code-${msgIdx}-${blockIdx}`,
              autoExecute: shouldAutoExecute,
              isReactComponent
            });
            
            // Agregar como acción de ejecución si es React
            if (shouldAutoExecute) {
              executeActions.push({
                type: 'preview',
                code: code,
                language: language,
                autoRun: true,
                id: `auto-preview-${msgIdx}-${blockIdx}`,
                raw: match[0],
                source: 'code-block'
              });
            }
          }
          blockIdx++;
        }
        
        // Usar el parser unificado de acciones JSON
        const actions = parseJSONActions(msg.content);
        
        // Procesar componentes dinámicos
        actions.component.forEach((comp, idx) => {
          if (COMPONENT_REGISTRY[comp.component] && 
              !dynamicComponents.find(c => c.component === comp.component)) {
            dynamicComponents.push({
              type: 'component',
              component: comp.component,
              props: comp.props,
              id: `component-${msgIdx}-${idx}`,
              raw: comp.raw
            });
          }
        });
        
        // Procesar acciones de ejecución de código
        actions.execute.forEach((exec, idx) => {
          executeActions.push({
            type: 'execute',
            code: exec.code,
            language: exec.language,
            autoRun: exec.autoRun,
            id: `execute-${msgIdx}-${idx}`,
            raw: exec.raw
          });
        });
        
        // Procesar acciones de diagramas desde JSON
        actions.diagram.forEach((diag, idx) => {
          // Evitar duplicados con diagramas ya detectados en markdown
          if (!diagrams.find(d => d.code === diag.content)) {
            diagrams.push({
              type: 'diagram',
              language: diag.diagramType,
              code: diag.content,
              title: diag.title || getDiagramTitle(diag.diagramType, diag.content),
              id: `diagram-json-${msgIdx}-${idx}`
            });
          }
        });
        
        // Procesar acciones de gráficos
        actions.chart.forEach((chart, idx) => {
          chartActions.push({
            type: 'chart',
            data: chart.data,
            chartType: chart.chartType,
            title: chart.title,
            config: chart.config,
            id: `chart-${msgIdx}-${idx}`,
            raw: chart.raw
          });
        });
        
        // También buscar en formato simplificado: COMPONENT:DamyxRainbow o [[DamyxRainbow]]
        const simpleComponentRegex = /(?:COMPONENT:|RENDER:|SHOW:|\[\[)(\w+)(?:\]\])?(?:\s*props:\s*(\{[^}]*\}))?/gi;
        let simpleMatch;
        
        while ((simpleMatch = simpleComponentRegex.exec(msg.content)) !== null) {
          const componentName = simpleMatch[1];
          let props = {};
          
          try {
            if (simpleMatch[2]) {
              props = JSON.parse(simpleMatch[2]);
            }
          } catch (e) {
            // Props inválidos, usar vacío
          }
          
          // Verificar si el componente está registrado y no está duplicado
          if (COMPONENT_REGISTRY[componentName] && 
              !dynamicComponents.find(c => c.component === componentName)) {
            dynamicComponents.push({
              type: 'component',
              component: componentName,
              props,
              id: `component-simple-${msgIdx}-${dynamicComponents.length}`,
              raw: simpleMatch[0]
            });
          }
        }
      }
    });
    
    return { codeBlocks, diagrams, dynamicComponents, executeActions, chartActions, imageBlocks };
  };

  const getExtension = (lang) => {
    const extensions = {
      javascript: 'js', js: 'js', typescript: 'ts', ts: 'ts',
      python: 'py', py: 'py', html: 'html', css: 'css',
      json: 'json', jsx: 'jsx', tsx: 'tsx', sql: 'sql',
      bash: 'sh', shell: 'sh', sh: 'sh', yaml: 'yaml',
      yml: 'yml', markdown: 'md', md: 'md', java: 'java',
      cpp: 'cpp', c: 'c', go: 'go', rust: 'rs',
      php: 'php', ruby: 'rb', swift: 'swift', kotlin: 'kt',
    };
    return extensions[lang?.toLowerCase()] || 'txt';
  };

  const handleCopy = async (code, idx) => {
    // Helper function for textarea fallback
    const copyWithTextarea = () => {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    };

    // Try modern clipboard API first
    let success = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(code);
        success = true;
      }
    } catch (clipErr) {
    }

    // Fallback to execCommand
    if (!success) {
      success = copyWithTextarea();
    }

    if (success) {
      setCopiedIdx(idx);
      toast.success(t('dashboard.copied'));
      setTimeout(() => setCopiedIdx(null), 2000);
    } else {
      toast.error(t('common.copyError'));
    }
  };

  const handleDownload = async (code, language, idx) => {
    try {
      const ext = getExtension(language);
      const filename = `code_${idx + 1}.${ext}`;
      const blob = new Blob([code], { type: 'text/plain;charset=utf-8' });
      const result = await saveBlobWithPicker(blob, filename, 'text/plain');
      if (result === 'fallback') {
        toast.success(`${t('dashboard.download')}: ${filename}`);
      } else {
        toast.success(t('common.openedInNewTab') || 'Abierto en nueva pestaña');
      }
    } catch (err) {
      toast.error(t('common.downloadError'));
    }
  };

  const { codeBlocks, diagrams, dynamicComponents, executeActions, chartActions, imageBlocks } = getBlocks();
  const jsxPreviewCode = extractPreviewCode();
  const hasPreviewableCode = jsxPreviewCode && jsxPreviewCode.length > 100;
  
  // ==========================================
  // MODO SINGLE-BLOCK WORKSPACE
  // Solo mostrar el último bloque de cada tipo
  // ==========================================
  
  // WORKSPACE SIMPLIFICADO - Solo código ejecutable e imágenes
  // Componentes dinámicos DESHABILITADOS (solo se muestran si se piden explícitamente)
  const hasDynamicComponents = false; // Deshabilitado por solicitud del usuario
  
  // Acciones de ejecución DESHABILITADAS 
  const hasExecuteActions = false; // Deshabilitado por solicitud del usuario
  
  // Gráficos DESHABILITADOS
  const hasChartActions = false; // Deshabilitado por solicitud del usuario
  
  // DIAGRAMAS DESHABILITADOS - Solo se muestran si el usuario los pide explícitamente
  // const lastDiagram = diagrams.length > 0 ? diagrams[diagrams.length - 1] : null;
  // const hasDiagrams = lastDiagram !== null;
  const hasDiagrams = false; // Diagramas deshabilitados por solicitud del usuario
  
  // Tomar solo la última imagen - DESACTIVADO para evitar mostrar automáticamente
  // Las imágenes se mostrarán solo cuando el usuario las pase al área de trabajo
  // const lastImage = imageBlocks.length > 0 ? imageBlocks[imageBlocks.length - 1] : null;
  // const hasImages = lastImage !== null;
  const lastImage = null;
  const hasImages = false;
  
  // Detectar bloques de código ejecutables (React/JSX) - solo el último
  const allExecutableCodeBlocks = codeBlocks.filter(b => b.autoExecute || b.isReactComponent);
  const lastExecutableCodeBlock = allExecutableCodeBlocks.length > 0 ? allExecutableCodeBlocks[allExecutableCodeBlocks.length - 1] : null;
  const hasExecutableCode = lastExecutableCodeBlock !== null;
  
  // Para compatibilidad con el resto del código
  const executableCodeBlocks = lastExecutableCodeBlock ? [lastExecutableCodeBlock] : [];
  
  // WORKSPACE SIMPLIFICADO - Solo código ejecutable e imágenes cuando DamyxIA los genera
  const hasContent = hasPreviewableCode || hasExecutableCode || hasImages;
  
  // Mostrar avatar solo si hay mensajes en la conversación (diálogo activo)
  const hasActiveDialogue = currentMessages.length > 0;
  
  // Effect to save code version when code changes
  useEffect(() => {
    if (jsxPreviewCode && jsxPreviewCode !== lastSavedCode) {
      saveCodeVersion(jsxPreviewCode);
    }
  }, [jsxPreviewCode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Calcular tipos de contenido para el banner de sincronización (modo single-block)
  const contentTypes = [];
  if (hasPreviewableCode) contentTypes.push({ type: 'code', count: 1 });
  if (hasImages) contentTypes.push({ type: 'image', count: 1 });
  if (hasExecutableCode) contentTypes.push({ type: 'executable', count: 1 });
  
  // Métricas de productividad (calculadas del contenido)
  const productivityMetrics = {
    componentsGenerated: codeBlocks.filter(b => b.code.includes('function ') || b.code.includes('const ')).length,
    errorsFixed: 0, // Se podría calcular de los mensajes
    linesOfCode: codeBlocks.reduce((acc, b) => acc + b.code.split('\n').length, 0),
  };

  // Handle applying fixes from review
  const handleApplyFixes = (fixedFiles) => {
    toast.success(t('dashboard.fixesApplied') || 'Correcciones aplicadas');
  };

  const [workspaceStatus, setWorkspaceStatus] = useState('idle');
  const [showPublishModal, setShowPublishModal] = useState(false);
  
  // Usar hook centralizado para estado de suscripción
  const { data, isPremium, openBillingPortal, refresh: refreshSubscription } = useSubscription();

  // NOTA: previamente este useEffect ocultaba el banner Premium automatica-
  // mente al detectar isPremium=true. Lo removimos porque ahora el banner
  // contiene los botones de Cancelar / Reactivar suscripcion y DEBE permanecer
  // visible. La visibilidad sigue siendo controlable manualmente por el
  // toggle de la barra superior + localStorage `damyxia_hide_subscription`.


  // Handle manage subscription (Billing Portal) - usa el hook
  const handleManageSubscription = async () => {
    // NOTA: ya NO ocultamos el widget al gestionar — porque el banner ahora
    // contiene los botones Cancelar/Reactivar y debe seguir accesible.
    try {
      await openBillingPortal();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al abrir portal de facturación' : 'Error opening billing portal');
    }
  };

  // ============ CANCEL / REACTIVATE SUBSCRIPTION ============
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isSubActionLoading, setIsSubActionLoading] = useState(null);

  const canCancelSubscription = !!(data && data.stripe_subscription_id && String(data.stripe_subscription_id).startsWith('sub_'));
  const subscriptionScheduledToCancel = !!(data && data.cancel_at_period_end);

  const handleCancelConfirm = async () => {
    const token = getToken();
    if (!token) return;
    setIsSubActionLoading('cancel');
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/cancel`,
        {},
        {}
      );
      toast.success(
        language === 'es'
          ? 'Tu suscripción se canceló correctamente. Seguirás siendo Premium hasta el final del período.'
          : 'Your subscription was canceled. You will remain Premium until the end of the period.',
        { duration: 7000 }
      );
      setShowCancelModal(false);
      await refreshSubscription();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (language === 'es' ? 'No se pudo cancelar la suscripción' : 'Could not cancel the subscription'));
    } finally {
      setIsSubActionLoading(null);
    }
  };

  const handleReactivate = async () => {
    const token = getToken();
    if (!token) return;
    setIsSubActionLoading('reactivate');
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/reactivate`,
        {},
        {}
      );
      toast.success(
        language === 'es'
          ? 'Tu suscripción fue reactivada. Continuará renovándose normalmente.'
          : 'Your subscription was reactivated. It will continue renewing normally.',
        { duration: 6000 }
      );
      await refreshSubscription();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (language === 'es' ? 'No se pudo reactivar la suscripción' : 'Could not reactivate'));
    } finally {
      setIsSubActionLoading(null);
    }
  };

  // Publicar app
  const handlePublishApp = async (data) => {
    try {
      const token = getToken();
      if (!token) {
        toast.error(t('common.loginRequired') || 'Login required');
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/api/published-apps/publish`,
        data,
        {}
      );
      
      toast.success(
        `${t('appPublish.publishedUrl')} ${response.data.url}`
      );
      setShowPublishModal(false);
    } catch (error) {
      toast.error(t('appPublish.errorPublishing'));
      throw error;
    }
  };

  // Actualizar estado cuando hay contenido
  useEffect(() => {
    if (hasPreviewableCode) {
      setWorkspaceStatus('generated');
    } else if (hasContent) {
      setWorkspaceStatus('updated');
    } else {
      setWorkspaceStatus('idle');
    }
  }, [hasPreviewableCode, hasContent]);

  // CUADRO NEGRO — state del Explorador de Archivos Generados (drawer
  // lateral). Solo se abre cuando el usuario clickea "Ver Archivos
  // Generados" en el panel del Modo Alternativo.
  const [showGeneratedFiles, setShowGeneratedFiles] = useState(false);
  const handleWorkspaceShowFiles = () => setShowGeneratedFiles(true);

  // 🟥 Archivos del proyecto activo — usados para "Guardar en GitHub" (Premium).
  const workspaceProjectFiles = useMemo(() => {
    if (!currentConversation) return [];
    return extractConversationFiles(currentConversation.messages || []);
  }, [currentConversation]);

  // 🟥 Notifico al header (vía CustomEvent) los archivos del proyecto activo
  // para que el LED del botón GitHub parpadee en verde cuando hay proyecto.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('damyxia:active-project', {
        detail: { files: workspaceProjectFiles },
      }));
    } catch { /* ignore */ }
  }, [workspaceProjectFiles]);

  // CUADRO NEGRO — handler "Descargar Proyecto" del Modo Alternativo del
  // Workspace. Reusa `downloadConversationAsProject` igual que ChatPanel
  // para mantener un único punto de descarga.
  const handleWorkspaceDownloadProject = async () => {
    if (!currentConversation) {
      toast.error(language === 'es' ? 'No hay proyecto activo' : 'No active project');
      return;
    }
    const messages = currentConversation.messages || [];
    if (messages.length === 0) {
      toast.error(language === 'es' ? 'Sin mensajes en la conversación' : 'No messages in conversation');
      return;
    }
    try {
      const projectName = currentConversation.title
        ? currentConversation.title.replace(/[^a-zA-Z0-9]/g, '_')
        : (language === 'es' ? 'proyecto' : 'project');
      const fileCount = await downloadConversationAsProject(messages, projectName);
      toast.success(
        (language === 'es' ? 'Proyecto descargado: ' : 'Project downloaded: ') + fileCount +
        (language === 'es' ? ' archivos' : ' files'),
      );
    } catch (err) {
      toast.error(language === 'es' ? 'No hay código para descargar' : 'No code to download');
    }
  };

  return (
    <div
      className="h-full flex flex-col overflow-hidden relative"
      style={{ backgroundColor: COLORS.background }}
    >
      {/* ========== HEADER: Área de Trabajo (P3.1 refactor → WorkspaceTopBar) ========== */}
      <WorkspaceTopBar
        isWorkspaceExpanded={isWorkspaceExpanded}
        onExpandWorkspace={onExpandWorkspace}
        jsxPreviewCode={jsxPreviewCode}
        lastExecutableCodeBlock={lastExecutableCodeBlock}
        setShowPublishModal={setShowPublishModal}
        showSubscriptionWidget={showSubscriptionWidget}
        setShowSubscriptionWidget={setShowSubscriptionWidget}
        isCreator={isCreator}
        data={data}
        latestWorkspaceImage={latestWorkspaceImage}
        showImageEditor={showImageEditor}
        workspaceImages={workspaceImages}
        workspaceVariants={workspaceVariants}
        setShowGalleryFromHeader={setShowGalleryFromHeader}
        isPremium={isPremium}
        trashBadgeCount={trashBadgeCount}
      />

      {/* ========== PREMIUM WELCOME BANNER (P3.1 refactor → PremiumBanner) ========== */}
      <PremiumBanner
        isPremium={isPremium}
        showSubscriptionWidget={showSubscriptionWidget}
        latestWorkspaceImage={latestWorkspaceImage}
        showImageEditor={showImageEditor}
        forceInternalWorkspace={forceInternalWorkspace}
        data={data}
        canCancelSubscription={canCancelSubscription}
        subscriptionScheduledToCancel={subscriptionScheduledToCancel}
        isSubActionLoading={isSubActionLoading}
        onManageSubscription={handleManageSubscription}
        onReactivate={handleReactivate}
        onRequestCancel={() => setShowCancelModal(true)}
      />

      {/* Modal de confirmacion cancelar (P3.1 refactor → CancelSubscriptionModal) */}
      <CancelSubscriptionModal
        open={showCancelModal}
        isSubActionLoading={isSubActionLoading}
        onConfirm={handleCancelConfirm}
        onClose={() => setShowCancelModal(false)}
      />

      {/* ========== ACCESO RÁPIDO A PIPELINES IA Y AUTOMATIONS IA ========== */}
      <QuickAccessPremium
        isPremium={isPremium}
        showSubscriptionWidget={showSubscriptionWidget}
        latestWorkspaceImage={latestWorkspaceImage}
        showImageEditor={showImageEditor}
        forceInternalWorkspace={forceInternalWorkspace}
        workspaceImages={workspaceImages}
      />

      {/* ========== SUBSCRIPTION WIDGET ========== */}
      {/* El toggle controla la visibilidad - siempre muestra si showSubscriptionWidget es true */}
      {showSubscriptionWidget && (
        <div
          className="flex-shrink-0 px-4 py-3 border-b border-slate-700/50"
          data-testid="subscription-widget-container"
        >
          <SubscriptionWidget />
        </div>
      )}

      {/* ========== CONTENIDO PRINCIPAL - ÁREA DE TRABAJO ÚNICA (P3.1 → WorkspaceMainContent) ========== */}
      <WorkspaceMainContent
        latestWorkspaceImage={latestWorkspaceImage}
        showImageEditor={showImageEditor}
        getVariantsForItem={getVariantsForItem}
        onSelectVariant={handleSelectVariant}
        onDeleteWorkspaceItem={handleDeleteWorkspaceItem}
        onCloseImageEditor={() => {
          setShowImageEditor(false);
          setLatestWorkspaceImage(null);
          setUserClosedImageViewer(true);
        }}
        onRefreshImages={() => loadWorkspaceImages()}
        onSelectFromGallery={(image) => {
          setLatestWorkspaceImage(image);
          setShowImageEditor(true);
          setUserClosedImageViewer(false);
        }}
        forceInternalWorkspace={forceInternalWorkspace}
        onInternalWorkspaceClosed={onInternalWorkspaceClosed}
        currentConversation={currentConversation}
        hasContent={hasContent}
        hasExecutableCode={hasExecutableCode}
        lastExecutableCodeBlock={lastExecutableCodeBlock}
        hasPreviewableCode={hasPreviewableCode}
        jsxPreviewCode={jsxPreviewCode}
        hasImages={hasImages}
        lastImage={lastImage}
        onDownloadProject={handleWorkspaceDownloadProject}
        onShowFiles={handleWorkspaceShowFiles}
        projectFiles={workspaceProjectFiles}
      />

      {/* ========== DAMYXIA WATERMARK ========== */}
      <div
        className="absolute bottom-4 right-4 flex items-center gap-1.5 opacity-40 hover:opacity-80 transition-opacity cursor-default z-10"
        title={t('dashboard.poweredBy')}
      >
        <div
          className="w-5 h-5 rounded-full overflow-hidden"
          style={{ boxShadow: '0 0 8px rgba(99, 102, 241, 0.3)' }}
        >
          <img src="/icons/icon-192x192.png" alt="DamyxIA" className="w-full h-full object-cover" />
        </div>
        <span
          className="text-[10px] font-medium"
          style={{
            background: 'linear-gradient(90deg, #a78bfa, #6366f1)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          DamyxIA
        </span>
      </div>

      {/* Publish App Modal */}
      <PublishAppModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onPublish={handlePublishApp}
        initialCode={jsxPreviewCode || ''}
        conversationId={currentConversation?.id}
      />

      {/* Code History Modal */}
      <CodeHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        versions={codeVersions}
        onRestore={handleRestoreVersion}
        onDelete={handleDeleteVersion}
        currentCode={jsxPreviewCode}
        language={language}
      />

      {/* Image Gallery Modal - Desde el header cuando no hay imagen activa */}
      <ImageGalleryModal
        isOpen={showGalleryFromHeader}
        onClose={() => setShowGalleryFromHeader(false)}
        onSelectImage={(image) => {
          // Si la imagen es pendiente, no hacer nada
          if (image.type === 'IMAGE_READY_FOR_WORK') {
            toast.info(t('appPublish.notPublished'));
            return;
          }
          // Seleccionar la imagen y mostrar el editor
          setLatestWorkspaceImage(image);
          setShowImageEditor(true);
          setUserClosedImageViewer(false); // Reset porque el usuario eligió una imagen
          setShowGalleryFromHeader(false);
        }}
        onImageDeleted={(imageId) => {
          // Actualizar estado si la imagen borrada era la activa
          if (latestWorkspaceImage?.id === imageId) {
            setLatestWorkspaceImage(null);
            setShowImageEditor(false);
            setUserClosedImageViewer(true);
          }
          // Actualizar listas locales
          setWorkspaceImages((prev) => prev.filter((img) => img.id !== imageId));
          setWorkspaceVariants((prev) =>
            prev.filter((v) => v.id !== imageId && v.parent_id !== imageId),
          );
        }}
        language={language}
      />

      {/* CUADRO NEGRO — Explorador de Archivos Generados (drawer derecho).
          Solo se abre al clickear "Ver Archivos Generados" en el panel
          del Modo Alternativo. Solo lectura. */}
      <GeneratedFilesDrawer
        open={showGeneratedFiles}
        onClose={() => setShowGeneratedFiles(false)}
        messages={(currentConversation && currentConversation.messages) || []}
        language={language}
      />
    </div>
  );
};

export default Dashboard;

